<template>
  <div class="reco-page client-page">
    <div class="reco-page__content">
      <header class="client-page__header">
        <h1 class="client-page__title">Aide</h1>
        <p class="client-page__intro">
          Le guide de votre espace et les réponses aux questions les plus fréquentes.
          Si vous ne trouvez pas ce qu’il vous faut, votre conseiller reste à votre disposition.
        </p>
      </header>

      <section class="aide-section" aria-label="Guide d'utilisation">
        <h2 class="aide-section__title">Guide d’utilisation</h2>
        <div class="aide-guide-grid">
          <router-link
            v-for="item in guideItems"
            :key="item.to"
            :to="item.to"
            class="aide-guide-card"
          >
            <component :is="item.icon" class="aide-guide-card__icon" />
            <div>
              <h3 class="aide-guide-card__title">{{ item.title }}</h3>
              <p class="aide-guide-card__text">{{ item.text }}</p>
            </div>
          </router-link>
        </div>
      </section>

      <section class="aide-section" aria-label="Questions fréquentes">
        <h2 class="aide-section__title">Questions fréquentes</h2>
        <details v-for="(item, index) in faqItems" :key="index" class="aide-faq">
          <summary class="aide-faq__question">{{ item.question }}</summary>
          <p class="aide-faq__answer">{{ item.answer }}</p>
        </details>
      </section>

      <section class="aide-contact">
        <p>Une question qui reste sans réponse ?</p>
        <router-link to="/analyse-patrimoniale/contact" class="client-btn client-btn--primary">
          Contacter mon conseiller
        </router-link>
      </section>

      <details class="aide-unsubscribe" aria-label="Désabonnement">
        <summary class="aide-unsubscribe__summary">Se désabonner</summary>
        <p class="aide-unsubscribe__text">
          La résiliation n’est pas encore en libre-service. En cliquant ci-dessous, une demande de
          désabonnement est transmise directement à votre conseiller, qui vous confirmera la date de fin d’accès.
        </p>
        <button
          type="button"
          class="client-btn client-btn--ghost aide-unsubscribe__btn"
          :disabled="unsubSending"
          @click="requestUnsubscribe"
        >
          {{ unsubSending ? 'Envoi en cours…' : 'Se désabonner' }}
        </button>
        <p v-if="unsubSent" class="aide-unsubscribe__feedback aide-unsubscribe__feedback--success" role="status">
          Votre demande de désabonnement a été transmise à votre conseiller.
        </p>
        <p v-if="unsubError" class="aide-unsubscribe__feedback aide-unsubscribe__feedback--error" role="alert">
          {{ unsubError }}
        </p>
      </details>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  ClipboardDocumentListIcon,
  ChartPieIcon,
  LightBulbIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/vue/24/outline';
import { isAuthenticatedSession } from '@/utils/sessionAuth';

const guideItems = [
  {
    to: '/analyse-patrimoniale/mon-analyse',
    icon: ClipboardDocumentListIcon,
    title: 'Mon analyse',
    text: 'Le récapitulatif de votre diagnostic patrimonial : actifs, charges et situation générale.',
  },
  {
    to: '/analyse-patrimoniale/analyses-sups',
    icon: ChartPieIcon,
    title: 'Résultats d’analyses',
    text: 'Le détail chiffré et les graphiques issus de votre diagnostic, une fois publiés par votre conseiller.',
  },
  {
    to: '/analyse-patrimoniale/mes-recommandations',
    icon: LightBulbIcon,
    title: 'Mes recommandations',
    text: 'Les priorités patrimoniales identifiées pour vous, classées par ordre d’importance.',
  },
  {
    to: '/analyse-patrimoniale/mes-actions',
    icon: ClipboardDocumentCheckIcon,
    title: 'Mes actions',
    text: 'Les tâches concrètes ajoutées par votre conseiller pour mettre en œuvre vos recommandations.',
  },
  {
    to: '/analyse-patrimoniale/contact',
    icon: ChatBubbleLeftRightIcon,
    title: 'Contacter mon conseiller',
    text: 'Un échange direct avec votre conseiller pour toute question sur votre dossier.',
  },
];

const faqItems = [
  {
    question: 'Qu’est-ce que le diagnostic patrimonial ?',
    answer:
      'C’est l’état des lieux complet de votre situation (revenus, charges, actifs, objectifs) que vous renseignez au démarrage. Il sert de base à l’analyse et aux recommandations de votre conseiller.',
  },
  {
    question: 'Combien de temps avant de recevoir mes résultats ?',
    answer:
      'Votre conseiller publie votre analyse après l’avoir étudiée. Le délai varie selon la complexité de votre dossier ; vous êtes notifié dès qu’elle est disponible dans « Résultats d’analyses ».',
  },
  {
    question: 'Quelle est la différence entre Mes recommandations et Mes actions ?',
    answer:
      '« Mes recommandations » présente les priorités patrimoniales identifiées pour vous. « Mes actions » liste les tâches concrètes que votre conseiller ajoute pour les mettre en œuvre, à cocher au fur et à mesure.',
  },
  {
    question: 'Quelles sont les modalités de paiement disponibles ?',
    answer:
      'Les offres Persona et Duo se règlent par mensualités après un premier versement. L’offre Pro se règle en 3 fois. Le paiement unique (avec remise) reste possible pour toutes les offres.',
  },
  {
    question: 'Comment modifier mes informations personnelles ?',
    answer:
      'Rendez-vous dans « Profil utilisateur » depuis le menu latéral pour mettre à jour vos coordonnées.',
  },
  {
    question: 'Comment me désabonner / résilier mon abonnement ?',
    answer:
      'Utilisez le bouton « Se désabonner » plus bas sur cette page : votre demande part directement à votre conseiller, qui vous confirmera la date de fin d’accès.',
  },
  {
    question: 'Je ne trouve pas de réponse à ma question, que faire ?',
    answer:
      'Utilisez « Contacter mon conseiller » : votre message lui parvient directement et il vous répondra au plus vite.',
  },
];

const unsubSending = ref(false);
const unsubSent = ref(false);
const unsubError = ref('');

async function requestUnsubscribe() {
  if (unsubSending.value) return;
  const confirm = await Swal.fire({
    title: 'Demande de désabonnement',
    text: 'Une demande sera transmise à votre conseiller, qui vous confirmera la date de fin d’accès. Continuer ?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Envoyer la demande',
    cancelButtonText: 'Annuler',
  });
  if (!confirm.isConfirmed) return;

  unsubSending.value = true;
  unsubSent.value = false;
  unsubError.value = '';
  try {
    if (!isAuthenticatedSession()) {
      unsubError.value = 'Session expirée. Veuillez vous reconnecter.';
      return;
    }
    const { data } = await axios.get('/api/conseillers/', { withCredentials: true });
    const list = Array.isArray(data) ? data : data?.results || data?.data || [];
    const employe = list[0];
    if (!employe?.id) {
      unsubError.value = 'Aucun conseiller disponible pour le moment. Réessayez plus tard.';
      return;
    }
    await axios.post(
      '/api/send-message/',
      {
        employe_id: employe.id,
        subject: 'Demande de désabonnement / résiliation',
        content:
          'Je souhaite me désabonner / résilier mon abonnement Patrimonea. Merci de me confirmer la date de fin d’accès.',
      },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    unsubSent.value = true;
  } catch (e) {
    unsubError.value = e?.response?.data?.error || 'Impossible d’envoyer la demande pour le moment.';
  } finally {
    unsubSending.value = false;
  }
}
</script>

<style scoped>
.reco-page {
  min-height: 100%;
  font-family: var(--font-body);
  color: var(--client-text);
  padding-bottom: 3rem;
}

.aide-section {
  margin-top: 2rem;
}

.aide-section__title {
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: var(--brand-brown);
  margin: 0 0 1rem;
}

.aide-guide-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
}

.aide-guide-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--paper);
  border: 1px solid var(--client-line, var(--line));
  border-radius: var(--radius-card);
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.aide-guide-card:hover,
.aide-guide-card:focus-visible {
  border-color: var(--brand-gold, #c9a227);
  box-shadow: 0 0 0 2px rgba(201, 162, 39, 0.18);
}

.aide-guide-card__icon {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  color: var(--brand-gold, #c9a227);
}

.aide-guide-card__title {
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--brand-navy);
  margin: 0 0 0.25rem;
}

.aide-guide-card__text {
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--client-text-muted, var(--muted));
  margin: 0;
}

.aide-faq {
  padding: 0.85rem 1rem;
  background: var(--paper);
  border: 1px solid var(--client-line, var(--line));
  border-radius: var(--radius-card);
  margin-bottom: 0.6rem;
}

.aide-faq__question {
  cursor: pointer;
  font-weight: 600;
  color: var(--brand-navy);
  list-style: none;
}

.aide-faq__question::-webkit-details-marker {
  display: none;
}

.aide-faq__question::before {
  content: '+';
  display: inline-block;
  width: 1rem;
  margin-right: 0.5rem;
  color: var(--brand-gold, #c9a227);
  font-weight: 700;
}

.aide-faq[open] .aide-faq__question::before {
  content: '−';
}

.aide-faq__answer {
  margin: 0.65rem 0 0 1.5rem;
  line-height: 1.5;
  color: var(--client-text-muted, var(--muted));
}

.aide-contact {
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--client-line);
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.aide-contact p {
  margin: 0;
  font-weight: 600;
  color: var(--brand-navy);
}

.aide-unsubscribe {
  margin-top: 2.5rem;
  padding: 0.85rem 1rem;
}

.aide-unsubscribe__summary {
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--client-text-muted, var(--muted));
  list-style: none;
}

.aide-unsubscribe__summary::-webkit-details-marker {
  display: none;
}

.aide-unsubscribe__summary::before {
  content: '+';
  display: inline-block;
  width: 1rem;
  margin-right: 0.35rem;
  color: var(--client-text-muted, var(--muted));
}

.aide-unsubscribe[open] .aide-unsubscribe__summary::before {
  content: '−';
}

.aide-unsubscribe__text {
  margin: 0.85rem 0 1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--client-text-muted, var(--muted));
}

.aide-unsubscribe__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.aide-unsubscribe__feedback {
  margin: 0.85rem 0 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.aide-unsubscribe__feedback--success {
  color: var(--success);
}

.aide-unsubscribe__feedback--error {
  color: var(--brand-red);
}
</style>
